import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Stethoscope,
    HeartPulse,
    Users,
    TrendingUp,
    Sparkles,
    Award,
    Lock,
} from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/ScrollReveal";
import MagneticButton from "@/components/MagneticButton";

const SITE_URL = "https://cursus-physio.fr";

// Premium generated visuals (nano_banana) — locally hosted
const IMG_HERO_CABINET = "/images/divers-debouches.jpg";
const IMG_LEGITIMITE = "/images/divers-legitimacy.jpg";
const IMG_GROWTH = "/images/divers-growth.jpg";

// ============ SECTION 1 — HERO ============
function HeroDiversification() {
    const { scrollY } = useScroll();
    const overlayY = useTransform(scrollY, [0, 600], [0, 80]);

    return (
        <section
            className="relative overflow-hidden bg-brand-ink text-white"
            data-testid="divers-hero"
        >
            <motion.div
                aria-hidden="true"
                style={{ y: overlayY }}
                className="pointer-events-none absolute -top-32 right-0 h-[640px] w-[640px] rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div className="absolute inset-0 grain pointer-events-none opacity-30" />

            <div className="relative mx-auto flex min-h-[80vh] max-w-7xl flex-col justify-center px-6 py-32 md:py-40 lg:py-48">
                <Reveal>
                    <span
                        className="inline-flex items-center self-start rounded-full border border-white/20 bg-white/[0.06] px-6 py-3 text-sm font-semibold uppercase tracking-[0.28em] text-white backdrop-blur sm:text-base"
                        data-testid="divers-eyebrow"
                    >
                        Diversification d&apos;activité
                    </span>
                </Reveal>

                <h1
                    className="mt-10 max-w-6xl font-display font-normal leading-[0.98] tracking-tightest"
                    data-testid="divers-title"
                >
                    <motion.span
                        initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                        className="block text-[32px] sm:text-5xl lg:text-[60px] xl:text-[68px]"
                    >
                        Diversification d&apos;activité en cabinet :
                    </motion.span>
                    <motion.span
                        initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
                        className="block text-[32px] sm:text-5xl lg:text-[60px] xl:text-[68px]"
                    >
                        une <em className="italic text-brand-orange">opportunité sans précédent</em>
                    </motion.span>
                    <motion.span
                        initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
                        className="block text-[32px] sm:text-5xl lg:text-[60px] xl:text-[68px]"
                    >
                        pour les <em className="italic text-brand-orange">kinésithérapeutes.</em>
                    </motion.span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.15 }}
                    className="mt-10 max-w-3xl text-lg leading-[1.65] text-white/75 md:text-xl"
                >
                    Le contexte n&apos;a jamais été aussi favorable.{" "}
                    <strong className="font-semibold text-white">Politique, économique, démographique</strong>{" "}
                    — tout converge vers la même conclusion : le moment pour les kinésithérapeutes de{" "}
                    <em className="italic text-brand-orange">diversifier leur activité</em>, avec la
                    Prévention Active et CURSUS PHYSIO, c&apos;est maintenant.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 1.3 }}
                    className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
                >
                    <MagneticButton strength={0.22}>
                        <Link
                            to="/sessions"
                            data-testid="divers-hero-cta-sessions"
                            className="group inline-flex items-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-orange/20 transition-shadow hover:shadow-xl hover:shadow-brand-orange/30"
                        >
                            Voir les sessions
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </MagneticButton>
                    <MagneticButton strength={0.18}>
                        <Link
                            to="/parler-conseiller"
                            data-testid="divers-hero-cta-contact"
                            className="group inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-sm font-semibold text-white transition-all hover:border-brand-orange hover:bg-brand-orange/10"
                        >
                            Parler à un conseiller
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </MagneticButton>
                </motion.div>
            </div>
        </section>
    );
}

// ============ Image bandeau (transition Hero → Section 2) ============
function CabinetImageBand() {
    return (
        <div className="relative bg-white" data-testid="divers-hero-image">
            <div className="mx-auto max-w-6xl px-6 py-14">
                <div className="relative w-full overflow-hidden rounded-3xl bg-[#F5F2EE]">
                    <img
                        src={IMG_HERO_CABINET}
                        alt="8 débouchés de la Prévention Active — Sport-Santé, Réathlétisation, Nutrition, Esthétique, Coaching Mental, Sophrologie, Ostéopathie, Bien-être Senior"
                        loading="eager"
                        fetchPriority="high"
                        decoding="async"
                        className="mx-auto block h-auto w-full max-w-4xl object-contain"
                    />
                </div>
            </div>
        </div>
    );
}

// ============ SECTION 2 — LE CONCEPT ============
function ConceptPA() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-concept"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Le concept
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[52px]">
                        Au cœur de cette opportunité,
                        <br className="hidden lg:block" />{" "}
                        un concept qui change tout :{" "}
                        <em className="italic text-brand-orange">la Prévention Active.</em>
                    </h2>
                    <p className="mx-auto mt-8 max-w-3xl text-base leading-[1.75] text-brand-ink/80 sm:text-[17px]">
                        La Prévention Active est un{" "}
                        <strong className="text-brand-ink">concept exclusif CURSUS PHYSIO</strong>. Il
                        repose sur la convergence de deux volontés complémentaires qui, ensemble, créent un
                        marché entièrement nouveau pour votre cabinet.
                    </p>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Reveal>
                        <div
                            className="flex h-full flex-col rounded-3xl border border-brand-orange/25 bg-[#F5F2EE] p-8 sm:p-10"
                            data-testid="concept-kine"
                        >
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange text-white shadow-lg">
                                <Stethoscope className="h-5 w-5" />
                            </span>
                            <h3 className="mt-6 font-display text-2xl font-normal leading-tight text-brand-ink sm:text-[26px]">
                                Côté <em className="italic text-brand-orange">kinésithérapeute</em>
                            </h3>
                            <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/80 sm:text-base">
                                Aller au-delà du soin ponctuel pour proposer un{" "}
                                <strong className="text-brand-ink">accompagnement global, continu et structuré</strong>{" "}
                                — et se différencier durablement.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div
                            className="flex h-full flex-col rounded-3xl border border-brand-ink/10 bg-white p-8 shadow-premium sm:p-10"
                            data-testid="concept-patient"
                        >
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-ink text-white shadow-lg">
                                <HeartPulse className="h-5 w-5" />
                            </span>
                            <h3 className="mt-6 font-display text-2xl font-normal leading-tight text-brand-ink sm:text-[26px]">
                                Côté <em className="italic text-brand-orange">patient / client</em>
                            </h3>
                            <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/80 sm:text-base">
                                S&apos;inscrire dans une{" "}
                                <strong className="text-brand-ink">routine vertueuse</strong> pour son
                                bien-être ou ses performances physiques : une demande croissante et non
                                satisfaite.
                            </p>
                        </div>
                    </Reveal>
                </div>

                {/* Bandeau de clôture */}
                <Reveal delay={0.3}>
                    <div className="mt-10 overflow-hidden rounded-3xl bg-[#33333A] px-8 py-10 text-center text-white sm:px-12 sm:py-12">
                        <p className="mx-auto max-w-4xl font-display text-2xl italic leading-snug sm:text-3xl lg:text-[32px]">
                            La <span className="not-italic text-brand-orange">Prévention Active</span> : au-delà de
                            l&apos;accompagnement global,{" "}
                            <em className="text-brand-orange">un nouveau modèle économique.</em>
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 3 — 3 SIGNAUX ============
const SIGNAUX = [
    {
        n: "01",
        title: "Le désengagement de la Sécurité Sociale",
        intro: "Ce n'est plus une menace, c'est une réalité. Les revenus conventionnés stagnent. Les négociations piétinent.",
        highlight:
            "La Prévention Active vous ouvre un espace de revenus Hors Convention entièrement libres, complémentaires, récurrents, maîtrisés.",
    },
    {
        n: "02",
        title: "Le vieillissement de la population active",
        intro: "Une population qui vieillit, qui veut rester en forme et en bonne santé le plus longtemps possible.",
        highlight:
            "La demande d'un accompagnement structuré en prévention, récupération et performance est là. La Prévention Active est exactement la réponse.",
    },
    {
        n: "03",
        title: "L'explosion du marché « Healthy »",
        intro: "Bien-être, prévention, optimisation de la santé, Sport-Santé… Des millions de Français cherchent un accompagnement sérieux, médical, structuré.",
        highlight:
            "La Prévention Active positionne le kinésithérapeute comme l'expert légitime de ce marché.",
    },
];

function TroisSignaux() {
    return (
        <section
            className="relative bg-[#F5F2EE] py-28 md:py-36"
            data-testid="section-signaux"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Signaux de marché
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[40px] lg:text-[52px]">
                        3 signaux qui rendent la Prévention Active incontournable
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">
                            sur un marché qui vous tend la main.
                        </em>
                    </h2>
                </Reveal>

                <StaggerGroup stagger={0.13} className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {SIGNAUX.map((s) => (
                        <StaggerItem key={s.n}>
                            <div
                                className="group flex h-full flex-col rounded-3xl border border-brand-ink/10 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/30 sm:p-10"
                                data-testid={`signal-${s.n}`}
                            >
                                <span className="font-display text-5xl italic text-brand-orange">{s.n}</span>
                                <h3 className="mt-6 font-display text-xl font-normal leading-tight text-brand-ink sm:text-[22px]">
                                    {s.title}
                                </h3>
                                <p className="mt-4 text-[15px] leading-relaxed text-brand-ink/70 sm:text-base">
                                    {s.intro}
                                </p>
                                <div className="mt-6 rounded-2xl border-l-2 border-brand-orange bg-brand-orange/5 px-4 py-3">
                                    <p className="text-[14px] font-medium leading-relaxed text-brand-ink sm:text-[15px]">
                                        {s.highlight}
                                    </p>
                                </div>
                            </div>
                        </StaggerItem>
                    ))}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 4 — LÉGITIMITÉ ============
function Legitimite() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-legitimite"
        >
            <div className="mx-auto max-w-7xl px-6">
                <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:items-center">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <div className="relative aspect-[4/5] overflow-hidden rounded-3xl">
                                <img
                                    src={IMG_LEGITIMITE}
                                    alt="Femme kinésithérapeute en blouse claire — cabinet clinique contemporain"
                                    loading="lazy"
                                    decoding="async"
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/50 via-transparent to-transparent" />
                                <div className="absolute inset-0 bg-brand-orange/[0.04] mix-blend-overlay" />
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={0.15}>
                            <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                                Légitimité
                            </span>
                            <h2 className="mt-8 font-display text-[30px] font-normal leading-[1.1] tracking-tightest text-brand-ink sm:text-[36px] lg:text-[44px]">
                                Qui est mieux placé qu&apos;un kinésithérapeute
                                <br className="hidden lg:block" />{" "}
                                pour accompagner la prévention et la performance ?{" "}
                                <em className="italic text-brand-orange">Personne.</em>
                            </h2>
                            <div className="mt-8 space-y-5 text-[15px] leading-[1.8] text-brand-ink/80 sm:text-base">
                                <p>
                                    Votre <strong className="text-brand-ink">formation universitaire</strong>, votre
                                    <strong className="text-brand-ink"> diplôme d&apos;État</strong>, votre expertise clinique
                                    et votre connaissance du corps humain vous placent{" "}
                                    <strong className="text-brand-ink">bien au-dessus de tout coach sportif</strong>.
                                </p>
                                <p>
                                    La Prévention Active n&apos;est{" "}
                                    <strong className="text-brand-orange">pas une nouvelle discipline à apprendre</strong>{" "}
                                    — c&apos;est votre terrain naturel, enfin structuré et monétisé.
                                </p>
                                <p>
                                    Pourtant, les coachs sportifs — sans formation médicale particulière — occupent
                                    aujourd&apos;hui cette activité parce qu&apos;ils ont su structurer une offre là où
                                    vous n&apos;aviez pas encore le système pour le faire.
                                </p>
                            </div>
                            <p className="mt-8 rounded-2xl border-l-4 border-brand-orange bg-brand-orange/5 px-6 py-5 font-display text-xl italic leading-snug text-brand-ink sm:text-2xl">
                                Avec la Prévention Active,{" "}
                                <span className="text-brand-orange">CURSUS PHYSIO</span> vous apporte le modèle
                                idéal pour ouvrir ou développer votre diversification d&apos;activité.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============ SECTION 5 — 5 BÉNÉFICES ============
const BENEFICES = [
    {
        icon: Lock,
        title: "Revenus Hors Convention",
        text: "Libres, récurrents et prévisibles, indépendants des décisions de l'Assurance Maladie.",
    },
    {
        icon: Users,
        title: "Fidélisation long terme",
        text: "Une patientèle fidélisée grâce au suivi mensuel de la Prévention Active.",
    },
    {
        icon: Award,
        title: "Positionnement premium",
        text: "Un statut d'Expert en Prévention Active qui vous distingue de vos confrères et des coachs sportifs.",
    },
    {
        icon: Sparkles,
        title: "Offre structurée et unique",
        text: "Que vos patients / clients ne trouveront nulle part ailleurs.",
    },
    {
        icon: TrendingUp,
        title: "Autonomie entrepreneuriale réelle",
        text: "Un cabinet qui vous ressemble et qui prospère.",
    },
];

function Benefices() {
    return (
        <section
            className="relative bg-[#F5F2EE] py-28 md:py-36"
            data-testid="section-benefices"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Bénéfices concrets
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[52px]">
                        Ce que la Prévention Active change concrètement
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">
                            pour vous et votre cabinet.
                        </em>
                    </h2>
                    <p className="mx-auto mt-8 max-w-3xl text-base leading-[1.75] text-brand-ink/80 sm:text-[17px]">
                        Intégrer la Prévention Active via CURSUS PHYSIO,{" "}
                        <strong className="text-brand-ink">ce n&apos;est pas tout réinventer</strong>. C&apos;est
                        diversifier votre activité — avec votre expertise — de manière{" "}
                        <em className="italic text-brand-orange">rentable et différenciante</em> par rapport à
                        ce que vous faites déjà avec vos patients.
                    </p>
                </Reveal>

                <StaggerGroup stagger={0.1} className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {BENEFICES.map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <StaggerItem key={b.title}>
                                <div
                                    className="flex h-full flex-col rounded-2xl border border-brand-ink/10 bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-brand-orange/30 sm:p-7"
                                    data-testid={`benefice-${i}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                            <Icon className="h-4 w-4" />
                                        </span>
                                        <span className="font-display text-2xl italic text-brand-orange">
                                            0{i + 1}
                                        </span>
                                    </div>
                                    <h3 className="mt-6 font-display text-lg font-normal leading-tight text-brand-ink sm:text-xl">
                                        {b.title}
                                    </h3>
                                    <p className="mt-3 text-[14px] leading-relaxed text-brand-ink/70 sm:text-[15px]">
                                        {b.text}
                                    </p>
                                </div>
                            </StaggerItem>
                        );
                    })}
                </StaggerGroup>
            </div>
        </section>
    );
}

// ============ SECTION 6 — LA QUESTION N'EST PLUS POURQUOI ============
function PunchQuestion() {
    return (
        <section
            className="relative overflow-hidden bg-[#33333A] py-32 text-white md:py-44 grain"
            data-testid="section-punch-question"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-40 right-0 h-[420px] w-[420px] rounded-full bg-brand-orange/10 blur-[140px]"
            />

            <div className="relative mx-auto max-w-5xl px-6 text-center">
                <Reveal>
                    <h2 className="font-display text-[32px] italic leading-[1.1] text-white sm:text-5xl lg:text-[64px] lg:leading-[1.08]">
                        La question n&apos;est plus «&nbsp;pourquoi diversifier&nbsp;?&nbsp;»
                    </h2>
                </Reveal>
                <Reveal delay={0.2}>
                    <h2 className="mt-4 font-display text-[32px] italic leading-[1.1] text-brand-orange sm:text-5xl lg:text-[64px] lg:leading-[1.08]">
                        mais «&nbsp;comment&nbsp;?&nbsp;»
                    </h2>
                </Reveal>
                <Reveal delay={0.4}>
                    <p className="mx-auto mt-12 max-w-3xl text-[15px] leading-[1.8] text-white/80 sm:text-base lg:text-lg">
                        Les kinésithérapeutes libéraux qui ont décidé de prendre leur avenir en main ont tous
                        le même constat :{" "}
                        <strong className="font-semibold text-white">
                            ils avaient les compétences
                        </strong>
                        . Il leur manquait{" "}
                        <strong className="font-semibold text-white">le système</strong> pour déployer la
                        Prévention Active de façon structurée, rentable et différenciante.
                    </p>
                </Reveal>
                <Reveal delay={0.55}>
                    <p className="mx-auto mt-10 max-w-3xl font-display text-xl italic leading-snug text-brand-orange sm:text-2xl lg:text-[28px]">
                        CURSUS PHYSIO a été conçu pour apporter aux kinés français le programme sur-mesure —
                        faire de la Prévention Active leur meilleure stratégie professionnelle.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 7 — TABLEAU CHIFFRÉ HC ============
const REVENUE_ROWS = [
    { clients: "10 clients", monthly: "750 €", six: "4 500 €", time: "2h30 à 3h20" },
    { clients: "20 clients", monthly: "1 500 €", six: "9 000 €", time: "5h à 6h40" },
    { clients: "30 clients", monthly: "2 250 €", six: "13 500 €", time: "7h30 à 10h" },
    { clients: "50 clients", monthly: "3 750 €", six: "22 500 €", time: "12h30 à 16h40" },
];

function RevenueTable() {
    return (
        <section
            className="relative bg-white py-28 md:py-36"
            data-testid="section-revenue"
        >
            <div className="mx-auto max-w-6xl px-6">
                <Reveal className="text-center">
                    <span className="inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-brand-orange">
                        Revenu additionnel
                    </span>
                    <h2 className="mx-auto mt-8 max-w-5xl font-display text-[32px] font-normal leading-[1.08] tracking-tightest text-brand-ink sm:text-[42px] lg:text-[52px]">
                        La Prévention Active avec CURSUS PHYSIO,
                        <br className="hidden lg:block" />{" "}
                        <em className="italic text-brand-orange">
                            votre nouveau revenu Hors Convention.
                        </em>
                    </h2>
                </Reveal>

                {/* Hypothèses */}
                <Reveal delay={0.15}>
                    <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
                        {[
                            { k: "Forfait mensuel", v: "75 € / client" },
                            { k: "Engagement minimum", v: "6 mois" },
                            {
                                k: "Temps de suivi",
                                v: "15 à 20 min / client / mois via l'application propriétaire CURSUS PHYSIO",
                            },
                        ].map((h) => (
                            <div
                                key={h.k}
                                className="rounded-2xl border border-brand-ink/10 bg-[#F5F2EE] p-5"
                            >
                                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                                    {h.k}
                                </div>
                                <div className="mt-3 font-display text-lg italic text-brand-ink sm:text-xl">
                                    {h.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* Tableau desktop */}
                <Reveal delay={0.3}>
                    <div
                        className="mt-12 hidden overflow-hidden rounded-3xl border border-brand-ink/10 md:block"
                        data-testid="revenue-table"
                    >
                        <div className="grid grid-cols-4 bg-brand-ink text-white">
                            <div className="p-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/85">
                                Nombre de clients
                            </div>
                            <div className="p-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/85">
                                Revenu mensuel
                            </div>
                            <div className="p-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                                Revenu sur 6 mois
                            </div>
                            <div className="p-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/85">
                                Temps de suivi / mois
                            </div>
                        </div>
                        {REVENUE_ROWS.map((r, i) => (
                            <div
                                key={r.clients}
                                className={`grid grid-cols-4 border-t border-brand-ink/10 ${
                                    i % 2 === 0 ? "bg-white" : "bg-[#F5F2EE]"
                                }`}
                            >
                                <div className="p-5 font-display text-lg font-semibold text-brand-ink">
                                    {r.clients}
                                </div>
                                <div className="p-5 text-brand-ink/80">{r.monthly}</div>
                                <div className="p-5 font-display text-xl italic text-brand-orange">
                                    {r.six}
                                </div>
                                <div className="p-5 text-sm text-brand-ink/70">{r.time}</div>
                            </div>
                        ))}
                    </div>
                </Reveal>

                {/* Mobile cards */}
                <div className="mt-12 grid grid-cols-1 gap-4 md:hidden">
                    {REVENUE_ROWS.map((r) => (
                        <div
                            key={r.clients}
                            className="rounded-2xl border border-brand-ink/10 bg-white p-5"
                        >
                            <div className="font-display text-2xl text-brand-ink">{r.clients}</div>
                            <div className="mt-3 flex flex-col gap-2 text-sm">
                                <div className="flex items-center justify-between border-b border-brand-ink/5 pb-2">
                                    <span className="text-brand-ink/60">Revenu mensuel</span>
                                    <span className="font-medium text-brand-ink">{r.monthly}</span>
                                </div>
                                <div className="flex items-center justify-between border-b border-brand-ink/5 pb-2">
                                    <span className="text-brand-ink/60">Revenu sur 6 mois</span>
                                    <span className="font-display text-lg italic text-brand-orange">
                                        {r.six}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-brand-ink/60">Temps / mois</span>
                                    <span className="text-brand-ink/80">{r.time}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Growth visual */}
                <Reveal delay={0.4}>
                    <div className="mt-14 grid grid-cols-1 items-stretch overflow-hidden rounded-3xl bg-[#F5F2EE] md:grid-cols-[2fr_3fr]">
                        <div className="relative min-h-[220px] overflow-hidden md:min-h-full">
                            <img
                                src={IMG_GROWTH}
                                alt="Croissance abstraite — trajectoire ascendante orange sur fond anthracite"
                                loading="lazy"
                                decoding="async"
                                className="h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#F5F2EE]/10 to-[#F5F2EE]" />
                        </div>
                        <div className="flex items-center p-8 sm:p-12">
                            <p className="text-xs italic leading-relaxed text-brand-ink/60 sm:text-sm">
                                Estimation à titre indicatif — sans tenir compte des opportunités auprès des
                                concepteurs et fabricants de solutions thérapeutiques permettant la mixité
                                d&apos;utilisations dédiées aux thérapeutiques, au bien-être / santé ou à la
                                performance.
                            </p>
                        </div>
                    </div>
                </Reveal>

                {/* Punchline */}
                <Reveal delay={0.5}>
                    <div className="mt-14 text-center">
                        <p className="font-display text-2xl leading-snug text-brand-ink sm:text-3xl lg:text-[36px]">
                            <strong className="font-normal">Vous avez la formation.</strong>{" "}
                            <strong className="font-normal">Vous avez la légitimité.</strong>
                        </p>
                        <p className="mt-3 font-display text-2xl italic leading-snug text-brand-orange sm:text-3xl lg:text-[36px]">
                            La Prévention Active vous apporte le système CURSUS PHYSIO.
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

// ============ SECTION 8 — CTA FINAL ============
function FinalCTA() {
    return (
        <section
            className="relative overflow-hidden bg-brand-ink py-32 text-white md:py-44 grain"
            data-testid="section-final-cta"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-orange/15 blur-[160px]"
            />
            <div className="relative mx-auto max-w-5xl px-6 text-center">
                <Reveal>
                    <span className="inline-flex items-center rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-white/90 backdrop-blur-sm">
                        <Users className="mr-2 h-3.5 w-3.5 text-brand-orange" />
                        Rejoignez la 1<sup className="text-[0.55em] align-super">re</sup>&nbsp;communauté
                        d&apos;Experts
                    </span>
                    <h2 className="mx-auto mt-8 max-w-4xl font-display text-[40px] font-normal leading-[1.04] tracking-tightest sm:text-5xl lg:text-7xl xl:text-[80px] lg:leading-[1.06]">
                        Devenez <em className="italic text-brand-orange">Expert</em>
                        <br className="hidden lg:block" />{" "}
                        en Prévention Active.
                    </h2>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-12 flex flex-col items-center justify-center gap-4">
                        <MagneticButton strength={0.28}>
                            <Link
                                to="/contact?subject=devenir-expert-pa"
                                data-testid="final-cta-devenir-expert"
                                className="group inline-flex items-center gap-3 rounded-full bg-brand-orange px-10 py-5 text-sm font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-brand-orange/25 transition-shadow hover:shadow-2xl hover:shadow-brand-orange/40 sm:text-base"
                            >
                                Je souhaite devenir Expert en Prévention Active
                                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </MagneticButton>
                        <Link
                            to="/sessions"
                            data-testid="final-cta-sessions"
                            className="mt-4 text-sm font-medium text-white/70 underline decoration-brand-orange/60 underline-offset-4 transition-colors hover:text-white hover:decoration-brand-orange"
                        >
                            Voir les prochaines sessions
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

// ============ PAGE ============
export default function DiversificationActivite() {
    return (
        <div data-testid="page-diversification-activite">
            <Helmet>
                <title>Diversification d&apos;activité — Prévention Active | Cursus Physio</title>
                <meta
                    name="description"
                    content="Diversifiez votre cabinet avec la Prévention Active. Revenus Hors Convention (jusqu'à 3 750 €/mois), positionnement premium, autonomie entrepreneuriale."
                />
                <link rel="canonical" href={`${SITE_URL}/diversification-activite`} />
                <meta property="og:title" content="Diversification d'activité — Cursus Physio" />
                <meta
                    property="og:description"
                    content="Le moment de diversifier votre activité avec la Prévention Active, c'est maintenant."
                />
                <meta property="og:url" content={`${SITE_URL}/diversification-activite`} />
            </Helmet>

            <HeroDiversification />
            <CabinetImageBand />
            <ConceptPA />
            <TroisSignaux />
            <Legitimite />
            <Benefices />
            <PunchQuestion />
            <RevenueTable />
            <FinalCTA />
        </div>
    );
}
